// An EMAIL in a Research Pack (busy-timeline-srv#35, AI Brain spec §9 row 5).
//
// A pack could hold a link, a photo, a snippet or a receipt — everything found on the web, and
// nothing the user already had. The one piece of evidence that usually starts a story, the mail
// itself, had no way in. The spec calls filing a thread into a pack STRONG evidence that the thread
// belongs to the story (§11, "direct Research Pack membership"), so saving one does two things:
// the pack gets a card, and the story gets the membership — timeline-srv writes the Ongoing→Timeline
// edge off the same save, with `research-pack` provenance.
//
// This module is the WEB half of that, and it is deliberately three tiny pure functions rather than
// logic inside the store action and the story view:
//
//   • what a ticked email on the Work-board becomes on the wire (`emailSaveItem`),
//   • how a saved item is recognised as an email coming back (`isEmailResearchItem`),
//   • what the pack card SAYS (`emailCard`).
//
// The two directions have to agree about which field is the subject and which is the sender — the
// server stores them as plain Title / Content snapshots, so nothing downstream can correct a mix-up
// — and they agree here, in one file, instead of in two views that look similar.

/** The item kind a saved email wears, on the board's way out and in the story's way back. */
export const EMAIL_KIND = 'email'

const text = (v) => String(v ?? '').trim()

/**
 * Is this Work-board row a ticked email THREAD?
 *
 * Threads only. A sender GROUP (`email-group`) and an ongoing's email CONNECTION
 * (`email-connection`) are on the same board and are equally ticked, but a sender is not a finding:
 * there is no one thing to open, and filing "everything LinkedIn ever sent" into a pack would claim
 * a membership over mail the user has not read. `emailId` is a timeline id (lib/emailSession.js's
 * note: a thread owns exactly one timeline for life), which is what makes a pick saveable at all.
 */
export function isEmailThreadPick (item) {
  return !!(item && item.kind === 'email-pick' && item.emailId)
}

/**
 * The research item one ticked email is saved as, or null when the row is not one.
 *
 * `title` is the SUBJECT and `content` the SENDER — snapshots, taken now, because a pack card has to
 * keep reading after the thread is archived or deleted. `timelineId` is the live half: it is what
 * "Open" resolves and what the story membership is written about.
 *
 * The subject falls back to the sender and then to a plain word: a card that says nothing at all is
 * worse than one that says "Email", and mail with an empty subject line is ordinary.
 */
export function emailSaveItem (item) {
  if (!isEmailThreadPick(item)) return null
  const sender = text(item.title)
  const subject = text(item.subject)
  return {
    kind: EMAIL_KIND,
    timelineId: String(item.emailId),
    title: subject || sender || 'Email',
    content: sender,
    query: item.query,
  }
}

/**
 * Is this saved research item an email coming back from the story?
 *
 * Both halves are asked, exactly as the server asks them: a kind with no thread id would draw an
 * "Open" that goes nowhere, and it is the id — not the word — that makes the card an email.
 */
export function isEmailResearchItem (r) {
  return !!(r && r.kind === EMAIL_KIND && r.timelineId)
}

/**
 * What a pack draws for a saved email: the sender, the subject, and the thread to open.
 *
 * Null for anything that is not one, so a view can map over a mixed list and skip the misses. The
 * sender line falls back to "Email" rather than to the subject: the two lines must not repeat each
 * other, and a card whose first line is blank reads as a broken row.
 */
export function emailCard (r) {
  if (!isEmailResearchItem(r)) return null
  return {
    id: r.id,
    timelineId: String(r.timelineId),
    sender: text(r.content) || 'Email',
    subject: text(r.title),
  }
}
